import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSync, faTrash } from "@fortawesome/free-solid-svg-icons"
import { URL, handleErrors } from "../../../store/actions/depannageActions"
import { showToast } from "../../../store/actions/toastActions"
import { COLORS } from "../../../utilities/Constants"
import { generalStyles as gs } from "../../styles/styles"
import CustomModal from "../../basics/interface/CustomModal"
import { filterEmployesActifs } from "../../../utilities/employes"

const styles = {
	wrap: { maxWidth: 720 },
	pickRow: { display: "flex", alignItems: "center", gap: 12, marginBottom: 24 },
	block: {
		backgroundColor: COLORS.surface,
		borderRadius: 14,
		border: `1px solid ${COLORS.border}`,
		padding: 16,
		marginBottom: 16,
	},
	blockHeader: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		marginBottom: 12,
	},
	blockTitle: { fontSize: 14, fontWeight: 600, color: COLORS.text },
	row: { display: "flex", alignItems: "center", gap: 10, marginBottom: 8 },
	trigger: {
		flex: 1,
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		padding: "8px 12px",
		border: `1px solid ${COLORS.border}`,
		borderRadius: 8,
		backgroundColor: COLORS.surface,
		fontSize: 13,
		color: COLORS.text,
		cursor: "pointer",
	},
	backdrop: { position: "fixed", inset: 0, zIndex: 55 },
	dropdown: {
		position: "absolute",
		top: "100%",
		left: 0,
		right: 0,
		marginTop: 4,
		zIndex: 60,
		maxHeight: 260,
		overflowY: "auto",
		backgroundColor: COLORS.surface,
		border: `1px solid ${COLORS.border}`,
		borderRadius: 10,
		boxShadow: "0 10px 30px rgba(28, 25, 23, 0.12)",
		padding: 4,
	},
	dropItem: {
		padding: "8px 10px",
		borderRadius: 8,
		cursor: "pointer",
		display: "flex",
		flexDirection: "column",
		gap: 4,
	},
	badges: { display: "flex", flexWrap: "wrap", gap: 6 },
	badge: {
		fontSize: 11,
		fontWeight: 600,
		color: COLORS.textMuted,
		backgroundColor: COLORS.bg,
		border: `1px solid ${COLORS.border}`,
		borderRadius: 99,
		padding: "2px 9px",
	},
	del: {
		border: `1px solid ${COLORS.dangerBorder}`,
		backgroundColor: COLORS.surface,
		color: COLORS.danger,
		borderRadius: 8,
		width: 32,
		height: 32,
		cursor: "pointer",
		flexShrink: 0,
	},
	addBtn: {
		border: `1px dashed ${COLORS.border}`,
		backgroundColor: COLORS.surface,
		color: COLORS.textMuted,
		borderRadius: 8,
		padding: "7px 14px",
		fontSize: 13,
		fontWeight: 600,
		cursor: "pointer",
		fontFamily: "inherit",
	},
	empty: { fontSize: 13, color: COLORS.textLight, padding: "6px 0" },
	footer: { display: "flex", justifyContent: "flex-end", marginTop: 8 },
}

// Option d'un BadgeSelect : libellé + badges des employés déjà assignés.
const Opt = ({ option, onPick }) => {
	const [hover, setHover] = useState(false)
	return (
		<div
			onClick={() => onPick(option.value)}
			onMouseEnter={() => setHover(true)}
			onMouseLeave={() => setHover(false)}
			style={{
				...styles.dropItem,
				backgroundColor: hover ? COLORS.bg : "transparent",
			}}
		>
			<span>{option.label || "—"}</span>
			{option.badges && option.badges.length > 0 && (
				<div style={styles.badges}>
					{option.badges.map((n, i) => (
						<span key={i} style={styles.badge}>
							{n}
						</span>
					))}
				</div>
			)}
		</div>
	)
}

// Select custom : un <select> natif ne peut pas afficher de badges dans son
// menu → dropdown maison qui rend chaque option avec ses badges.
const BadgeSelect = ({ value, options, onChange, placeholder = "—" }) => {
	const [open, setOpen] = useState(false)
	const selected = options.find((o) => String(o.value) === String(value))
	return (
		<div style={{ position: "relative", flex: 1 }}>
			<div style={styles.trigger} onClick={() => setOpen((o) => !o)}>
				<span>{selected ? selected.label : placeholder}</span>
				<span style={{ opacity: 0.5, marginLeft: 8 }}>▾</span>
			</div>
			{open && (
				<>
					<div style={styles.backdrop} onClick={() => setOpen(false)} />
					<div style={styles.dropdown}>
						{options.map((o) => (
							<Opt
								key={o.value}
								option={o}
								onPick={(val) => {
									onChange(val)
									setOpen(false)
								}}
							/>
						))}
					</div>
				</>
			)}
		</div>
	)
}

const AssignationV2 = () => {
	const dispatch = useDispatch()
	const token = useSelector((s) => s.navInfo.login.token)
	const employes = useSelector((s) => s.masterReducer.employes)
	const masterVehicules = useSelector((s) => s.masterReducer.masterVehicules)
	const taches = useSelector((s) => s.autreInfo.taches)

	const [employeId, setEmployeId] = useState("")
	const [tv, setTv] = useState([])
	const [ta, setTa] = useState([])
	const [tvOverview, setTvOverview] = useState({})
	const [taOverview, setTaOverview] = useState({})
	const [loading, setLoading] = useState(false)
	const [saving, setSaving] = useState(false)
	const [confirmSave, setConfirmSave] = useState(false)

	const flotte = (masterVehicules || [])
		.filter((v) => v.propre_vehicule)
		.map((v) => ({
			pvId: v.propre_vehicule.id,
			label: `${v.marque || ""} ${v.modele || ""} ${v.plaques || ""}`.trim(),
		}))

	// Options pour les BadgeSelect (label + badges « déjà assigné à »).
	const flotteOptions = flotte.map((v) => ({
		value: v.pvId,
		label: v.label,
		badges: tvOverview[v.pvId] || [],
	}))
	const tacheOptions = (taches || []).map((t) => ({
		value: t.id,
		label: t.texte || "—",
		badges: taOverview[t.id] || [],
	}))

	const auth = { Authorization: `Bearer ${token}` }
	const jsonHeaders = {
		...auth,
		Accept: "application/json, text/plain",
		"Content-type": "application/json",
	}

	const setLists = (json) => {
		setTv(json.tv || [])
		setTa(json.ta || [])
		setTvOverview(json.tv_overview || {})
		setTaOverview(json.ta_overview || {})
	}

	useEffect(() => {
		if (!employeId) {
			setTv([])
			setTa([])
			return
		}
		setLoading(true)
		fetch(`${URL}api/tachesoptions?employe=${employeId}`, { headers: auth })
			.then(handleErrors)
			.then((r) => r.json())
			.then(setLists)
			.catch(() => dispatch(showToast("danger", "Échec du chargement.")))
			.finally(() => setLoading(false))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [employeId])

	const addVeh = () =>
		setTv([
			...tv,
			{ id: 0, propre_vehicule: flotte.length ? flotte[0].pvId : 0 },
		])
	const updateVeh = (index, pvId) =>
		setTv(
			tv.map((row, i) =>
				i === index ? { ...row, propre_vehicule: pvId } : row,
			),
		)
	const addTa = () =>
		setTa([...ta, { id: 0, ta: taches && taches.length ? taches[0].id : 0 }])
	const updateTa = (index, taId) =>
		setTa(ta.map((row, i) => (i === index ? { ...row, ta: taId } : row)))

	const removeRow = (row, index, isTv) => {
		if (!row.id) {
			if (isTv) setTv(tv.filter((_, i) => i !== index))
			else setTa(ta.filter((_, i) => i !== index))
			return
		}
		fetch(`${URL}api/tachesoptions`, {
			method: "delete",
			headers: jsonHeaders,
			body: JSON.stringify({ emp: employeId, id: row.id, isTv }),
		})
			.then(handleErrors)
			.then((r) => r.json())
			.then(setLists)
			.catch(() => dispatch(showToast("danger", "Échec de la suppression.")))
	}

	const doSave = () => {
		setSaving(true)
		fetch(`${URL}api/tachesoptions`, {
			method: "post",
			headers: jsonHeaders,
			body: JSON.stringify({ emp: employeId, tv, ta }),
		})
			.then(handleErrors)
			.then((r) => r.json())
			.then((json) => {
				setLists(json)
				setConfirmSave(false)
				dispatch(showToast("success", "Assignation enregistrée"))
			})
			.catch(() =>
				dispatch(showToast("danger", "Échec de l'enregistrement.")),
			)
			.finally(() => setSaving(false))
	}

	return (
		<div style={styles.wrap}>
			{confirmSave && (
				<CustomModal
					title="Enregistrer les modifications ?"
					onClose={saving ? () => {} : () => setConfirmSave(false)}
					showCloseButton={!saving}
					buttons={[
						{
							title: "Annuler",
							styleName: "secondary",
							onClick: () => setConfirmSave(false),
							disabled: saving,
						},
						{
							title: saving ? (
								<FontAwesomeIcon icon={faSync} spin />
							) : (
								"Enregistrer"
							),
							styleName: "primary",
							onClick: doSave,
							disabled: saving,
						},
					]}
				>
					<div style={{ fontSize: 14, color: COLORS.textMuted }}>
						Les véhicules et tâches assignés à cet employé seront
						enregistrés.
					</div>
				</CustomModal>
			)}
			<div style={styles.pickRow}>
				<label style={gs.label}>Employé</label>
				<select
					style={gs.select}
					value={employeId}
					onChange={(e) => setEmployeId(e.target.value)}
				>
					<option value="">— Choisir un employé —</option>
					{filterEmployesActifs(employes, employeId).map((emp) => (
						<option key={emp.id} value={emp.id}>
							{emp.nom}
						</option>
					))}
				</select>
				{loading && <FontAwesomeIcon icon={faSync} spin />}
			</div>

			{!employeId ? null : (
				<>
					<div style={styles.block}>
						<div style={styles.blockHeader}>
							<span style={styles.blockTitle}>Véhicules</span>
							<button style={styles.addBtn} onClick={addVeh}>
								+ Ajouter un véhicule
							</button>
						</div>
						{tv.length === 0 ? (
							<div style={styles.empty}>Aucun véhicule assigné.</div>
						) : (
							tv.map((row, index) => (
								<div
									key={`tv_${index}_${row.id}`}
									style={styles.row}
								>
									<BadgeSelect
										value={row.propre_vehicule}
										options={flotteOptions}
										onChange={(val) => updateVeh(index, val)}
									/>
									<button
										style={styles.del}
										onClick={() => removeRow(row, index, true)}
									>
										<FontAwesomeIcon icon={faTrash} />
									</button>
								</div>
							))
						)}
					</div>

					<div style={styles.block}>
						<div style={styles.blockHeader}>
							<span style={styles.blockTitle}>Tâches (Base)</span>
							<button style={styles.addBtn} onClick={addTa}>
								+ Ajouter une tâche
							</button>
						</div>
						{ta.length === 0 ? (
							<div style={styles.empty}>Aucune tâche assignée.</div>
						) : (
							ta.map((row, index) => (
								<div
									key={`ta_${index}_${row.id}`}
									style={styles.row}
								>
									<BadgeSelect
										value={row.ta}
										options={tacheOptions}
										onChange={(val) => updateTa(index, val)}
									/>
									<button
										style={styles.del}
										onClick={() => removeRow(row, index, false)}
									>
										<FontAwesomeIcon icon={faTrash} />
									</button>
								</div>
							))
						)}
					</div>

					<div style={styles.footer}>
						<button
							style={gs.btnPrimary}
							onClick={() => setConfirmSave(true)}
						>
							Enregistrer
						</button>
					</div>
				</>
			)}
		</div>
	)
}

export default AssignationV2
